import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatSupabaseError } from "../lib/supabase";
import { LOGO_URL } from "../lib/data";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/admin/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Willkommen zurück.");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(formatSupabaseError(err, "Login fehlgeschlagen"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white flex items-center justify-center relative overflow-hidden grain">
      <div className="absolute -top-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-[#ff0033]/15 blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[30rem] h-[30rem] rounded-full bg-[#ff0033]/10 blur-[140px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-md px-6">
        <Link to="/" className="inline-flex items-center gap-3 mb-12 group" data-testid="admin-login-brand">
          <img
            src={LOGO_URL}
            alt="STRAßENWERT"
            className="h-12 w-auto drop-shadow-[0_0_14px_rgba(255,0,51,0.35)] group-hover:drop-shadow-[0_0_22px_rgba(255,0,51,0.6)] transition-all"
          />
        </Link>

        <div className="flex items-center gap-3 mb-6 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
          <span className="red-led" />
          Admin Zugang
        </div>

        <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl mb-10">
          Einloggen.
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate data-testid="admin-login-form">
          <label className="block">
            <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
              E-Mail
            </span>
            <div className="flex items-center gap-3 border-b border-white/15 focus-within:border-[#ff0033]/60 transition-colors">
              <Mail size={16} className="text-[#ff0033]" />
              <input
                data-testid="admin-login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-transparent py-3 outline-none text-white placeholder:text-zinc-600 font-medium"
                placeholder="management@strassenwert.de"
              />
            </div>
          </label>

          <label className="block">
            <span className="block text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
              Passwort
            </span>
            <div className="flex items-center gap-3 border-b border-white/15 focus-within:border-[#ff0033]/60 transition-colors">
              <Lock size={16} className="text-[#ff0033]" />
              <input
                data-testid="admin-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-transparent py-3 outline-none text-white placeholder:text-zinc-600 font-medium"
                placeholder="••••••••"
              />
            </div>
          </label>

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="group w-full relative inline-flex items-center justify-center gap-3 bg-white text-black px-7 py-4 text-[11px] font-bold tracking-[0.3em] uppercase overflow-hidden disabled:opacity-50 hover:shadow-[0_0_30px_rgba(255,0,51,0.4)] transition-all mt-6"
          >
            <span className="relative z-10 group-hover:text-white transition-colors">
              {loading ? "Prüfe…" : "Einloggen"}
            </span>
            <ArrowRight size={14} className="relative z-10 group-hover:text-white transition-colors" />
            <span className="absolute inset-0 bg-[#ff0033] translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
          </button>
        </form>

        <div className="mt-10 text-center">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition-colors"
          >
            ← Zurück zur Website
          </Link>
        </div>
      </div>
    </main>
  );
}
